import { useMemo, useState } from 'react';
import { useBrandSearch } from '../../hooks/useBrandSearch';
import type { WatchBrand } from '../../types';

interface BrandMultiSelectProps {
  /** Brand list — expected pre-sorted (e.g. alphabetically) by the caller. */
  brands: WatchBrand[];
  /** When true the list shows a loading line instead of options. */
  loading: boolean;
  /** Selected brand ids. */
  value: string[];
  /** Called with the full new selection. */
  onChange: (brandIds: string[]) => void;
  /** Disables the search box, every option and the chips. */
  disabled?: boolean;
  /** Field label (omit to render no label). */
  label?: string;
  /**
   * Fired the first time the picker is focused — lets a caller that has not
   * loaded the brand list yet do so on demand rather than on page load.
   */
  onFirstFocus?: () => void;
  className?: string;
}

/**
 * Multi-select counterpart to {@link BrandFilterSelect}: the same search box
 * (Algolia matching through `useBrandSearch`, substring fallback), over a
 * scrollable list of checkboxes, with the current selection shown as removable
 * chips so it stays visible while the list is filtered to something else.
 *
 * Callers own the selected ids and the brand list; the search term is local.
 */
export function BrandMultiSelect({
  brands,
  loading,
  value,
  onChange,
  disabled = false,
  label = 'Brands',
  onFirstFocus,
  className,
}: BrandMultiSelectProps) {
  const [search, setSearch] = useState('');
  const [focusedOnce, setFocusedOnce] = useState(false);
  const { results } = useBrandSearch(brands, search);
  const selected = useMemo(() => new Set(value), [value]);
  const brandById = useMemo(() => new Map(brands.map((b) => [b.id, b])), [brands]);
  const nameOf = (id: string) => {
    const brand = brandById.get(id);
    return brand ? brand.displayName || brand.name : id;
  };

  const toggle = (id: string) =>
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <div
      className={className}
      onFocusCapture={() => {
        if (focusedOnce) return;
        setFocusedOnce(true);
        onFirstFocus?.();
      }}
    >
      {label && <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>}

      {value.length > 0 && (
        <ul aria-label="Selected brands" className="mb-2 flex flex-wrap gap-1.5">
          {value.map((id) => (
            <li
              key={id}
              className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs text-brand-800"
            >
              {nameOf(id)}
              <button
                type="button"
                disabled={disabled}
                onClick={() => toggle(id)}
                aria-label={`Remove ${nameOf(id)}`}
                className="text-brand-600 hover:text-brand-900 disabled:opacity-50"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        type="text"
        value={search}
        disabled={disabled}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search brands…"
        aria-label="Search brands"
        className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 disabled:bg-gray-100 disabled:text-gray-400"
      />

      <div
        role="group"
        aria-label="Brand options"
        className={`max-h-48 overflow-y-auto rounded-lg border border-gray-300 ${disabled ? 'bg-gray-100 opacity-60' : 'bg-white'}`}
      >
        {loading ? (
          <p className="px-3 py-2 text-sm text-gray-500">Loading brands…</p>
        ) : brands.length === 0 ? (
          <p className="px-3 py-2 text-sm text-gray-500">
            {focusedOnce ? 'No brands found.' : 'Click the search box to load the brand list.'}
          </p>
        ) : results.length === 0 ? (
          <p className="px-3 py-2 text-sm text-gray-500">No brand matches “{search}”.</p>
        ) : (
          results.map((b) => (
            <label
              key={b.id}
              className="flex cursor-pointer items-center gap-2 px-3 py-1 text-sm text-gray-800 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={selected.has(b.id)}
                disabled={disabled}
                onChange={() => toggle(b.id)}
                className="rounded text-brand-600 focus:ring-brand-500"
              />
              <span>{b.displayName || b.name}</span>
              <span className="text-xs text-gray-400">{b.id}</span>
            </label>
          ))
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {value.length} selected
        {brands.length > 0 ? ` · ${results.length} of ${brands.length} shown` : ''}
      </p>
    </div>
  );
}
